/**
 * QuarterlyUpdateModal Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A modal component for displaying quarterly update information with:
 * - Card and table view modes for data visualization
 * - Quarter derivation from month data when missing
 * - Data processing for consistent quarter formatting
 * - Tabbed interface for different viewing options
 * - Close functionality with modal header
 * - Responsive design for various screen sizes
 * - Empty data handling with graceful return
 * 
 * @param {string} title - Title for the modal header
 * @param {Array} data - Array of quarterly update data objects
 * @param {function} onClose - Callback function when modal is closed
 * @returns {JSX.Element|null} Modal component or null if no data
 */
import React from 'react';
import './QuarterlyUpdateModal.css';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import QuarterlyUpdateTable from '../components/PolicyRegulatory/QuarterlyUpdateTable';
import QuarterlyUpdateCard from '../components/PolicyRegulatory/QuarterlyUpdateCard';

/**
 * QuarterlyUpdateModal component for displaying quarterly data
 * with multiple view modes and data processing capabilities
 */
function QuarterlyUpdateModal({ title, data, onClose }) {
    if (!data || data.length === 0) return null;

    // Function to derive quarter from month name
    const getQuarterFromMonth = (month) => {
        if (!month) return '';
        const monthIndex = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'].indexOf(month);
        if (monthIndex === -1) return '';
        return `Q${Math.floor(monthIndex / 3) + 1}`;
    };

    // Process data to add missing quarters
    const processedData = data.map((item) => ({
        ...item,
        // If Quarter is missing but Month exists, derive it from Month
        Quarter: item.Quarter && item.Quarter.trim() !== '' 
            ? item.Quarter 
            : getQuarterFromMonth(item.Month || item.month)
    }));

    return (
        <div className="quarterlyUpdate-container">
            <div className="modal-content">
                <div className="quarterlyUpdate-header">
                    <h3>{title}</h3>
                    <i title='Close' onClick={onClose} className="modalclose fa-solid fa-xmark pointer"></i>
                </div>

                <div className="quarterlyUpdate-body">
                    <Tabs>
                        <TabList>
                            <Tab><i className="fa-solid fa-border-all"></i> Card View</Tab>
                            <Tab><i className="fa-solid fa-table-cells"></i> Table View</Tab>
                        </TabList>

                        <TabPanel>
                            <QuarterlyUpdateCard data={processedData} />
                        </TabPanel>

                        <TabPanel>
                            <QuarterlyUpdateTable data={processedData} />
                        </TabPanel>
                    </Tabs>
                </div>
            </div>
        </div>
    );
}

export default QuarterlyUpdateModal;
